// src/pages/NexioraInsightPage.js
// ═══════════════════════════════════════════════════════════════════════════
// NEXIORA INSIGHT — riprogettata ottobre 2026 (audit approvato da Paolo)
// Risponde a tre domande, senza duplicare le sezioni di dettaglio:
//   1. cosa richiede attenzione oggi → «Priorità operative» (5 numeri, definizioni uniche in
//      utils/prioritaOperative.js, gli stessi elenchi che apre il Centro Operativo);
//   2. cosa rischia di compromettere attività o commesse → «Da sistemare» (un elenco compatto);
//   3. come va l'economia delle commesse aperte → margine previsto complessivo, con la
//      formula unica di utils/economiaCommessa.js (marginePrevistoCommessa), le varianti in
//      attesa di accettazione a parte e la variazione rispetto all'ultima rilevazione salvata.
// Ogni problema compare una volta sola. Riservata a titolare e admin (dati economici).
// ═══════════════════════════════════════════════════════════════════════════
import { statoNf } from "../utils/acquisti";
import React, { useEffect, useState, useMemo, useRef } from "react";
import { collection, query, where, onSnapshot, doc, getDoc, setDoc, deleteField } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { RefreshCw, ChevronRight } from "lucide-react";
import SezioneCommerciale from "./CentroCommercialePage";
import { usePiano, BadgePiano } from "../utils/usePiano";
import { toLocalDateStr } from "../utils/dateUtils";
import { economiaSintetica, marginePrevistoCommessa, commessaAperta, numIt, imponibileOda, dataAggiornamentoCosti, testoAggiornamentoCosti } from "../utils/economiaCommessa";
import { fonteOreAzienda } from "../utils/oreCommessa";
import { riepilogoVarianti, stessoRiepilogo } from "../utils/varianti";
import { sincronizzaVariantiSuBudget } from "../utils/variantiSync";
import { vociCommesseAperte, elenchiPriorita } from "../utils/prioritaOperative";
import { statoIter, avanzamento, righePratica, oggiIso } from "../utils/pratiche";
import { scadenzaNoleggio, rettificheDaVedere, testoRettifica, etichettaNoleggio } from "../utils/noleggiUtils";

// ── Colori (proposta A «viola minimale»: il viola solo su fascia, titoli e link) ──
const C = {
  fascia: "#6d28d9", titolo: "#5b21b6", link: "#6d28d9", testo: "#374151", tenue: "#6b7280",
  bordo: "#e5e7eb", rosso: "#dc2626", rossoFondo: "#fef2f2", arancio: "#d97706", verde: "#059669", sfondo: "#fafafa",
};
// Punto delle migliaia sempre, anche sotto 10.000 (toLocaleString italiano scrive «9500»)
const euro = n => { const v = Math.round(Number(n) || 0); return (v < 0 ? "−€ " : "€ ") + String(Math.abs(v)).replace(/\B(?=(\d{3})+(?!\d))/g, "."); };
const dataBreve = iso => iso ? iso.split("-").reverse().slice(0, 2).join("/") : "";
const p2 = n => String(n).padStart(2, "0");
const PESO = { critico: 0, alto: 1, medio: 2 };
const VUOTO = [];

function useLarghezza() {
  const [w, setW] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 1200));
  useEffect(() => {
    const f = () => setW(window.innerWidth);
    window.addEventListener("resize", f);
    return () => window.removeEventListener("resize", f);
  }, []);
  return w;
}

function Scheda({ titolo, destra, children, style }) {
  return (
    <div style={{ background: "#fff", border: `1px solid ${C.bordo}`, borderRadius: 12, padding: "16px 18px", ...style }}>
      {(titolo || destra) && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 12 }}>
          <div style={{ fontSize: 13.5, fontWeight: 800, color: C.titolo }}>{titolo}</div>
          {destra}
        </div>
      )}
      {children}
    </div>
  );
}

// Un indicatore della riga «Priorità operative»: numero + etichetta, cliccabile.
// Rosso solo per i ritardi; a zero diventa un segno di spunta grigio.
function Priorita({ valore, etichetta, sotto, rosso, onClick, titoloAiuto }) {
  const zero = !valore;
  const allarme = rosso && !zero;
  return (
    <button onClick={zero ? undefined : onClick} title={titoloAiuto} disabled={zero}
      style={{ textAlign: "left", background: allarme ? C.rossoFondo : "#fff", border: `1px solid ${allarme ? "#fca5a5" : C.bordo}`,
        borderRadius: 10, padding: "10px 12px", cursor: zero ? "default" : "pointer", fontFamily: "'Inter',sans-serif", minWidth: 0 }}>
      <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.1, color: zero ? "#9ca3af" : allarme ? C.rosso : "#111827" }}>
        {zero ? "✓" : valore}
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: zero ? "#9ca3af" : C.testo, marginTop: 3 }}>{etichetta}</div>
      {sotto && !zero && <div style={{ fontSize: 10.5, color: allarme ? C.rosso : C.tenue, marginTop: 1 }}>{sotto}</div>}
    </button>
  );
}

export default function NexioraInsightPage({ onNavigate }) {
  const { isOrgAdmin, isPlatformOwner, userProfile } = useAuth();
  const { effectiveOrgId, forcedOrgId, orgVisitata } = useOrg();
  const { piano, giorniRimasti } = usePiano(effectiveOrgId);
  const larghezza = useLarghezza();
  const autorizzato = !!(isOrgAdmin || isPlatformOwner);
  // Platform Owner entrato in un'altra azienda: si guarda, non si scrive nulla
  const inVisita = !!forcedOrgId && forcedOrgId !== userProfile?.organizationId;

  const [d, setD] = useState({});           // collezione → elenco documenti
  const [arrivati, setArrivati] = useState({}); // collezione → primo caricamento avvenuto
  const [aggiornatoAlle, setAggiornatoAlle] = useState(null);
  const [mostraTutto, setMostraTutto] = useState(false);
  const [rilevazioni, setRilevazioni] = useState(null);

  // ── Dati: un ascolto per collezione, sempre filtrato sull'azienda ─────────────
  useEffect(() => {
    if (!effectiveOrgId || !autorizzato) return;
    setD({}); setArrivati({});
    const o = effectiveOrgId;
    const fonti = [
      ["projects", query(collection(db, "projects"), where("organizationId", "==", o))],
      ["tasks", query(collection(db, "tasks"), where("organizationId", "==", o))],
      ["subtasks", query(collection(db, "subtasks"), where("organizationId", "==", o))],
      ["scadenzario", query(collection(db, "scadenzario"), where("organizationId", "==", o))],
      ["noteSpese", query(collection(db, "noteSpese"), where("orgId", "==", o))],
      ["movimenti", query(collection(db, "movimentiEconomici"), where("organizationId", "==", o))],
      ["budget", query(collection(db, "budgetCommessa"), where("organizationId", "==", o))],
      ["ordini", query(collection(db, "ordiniAcquisto"), where("organizationId", "==", o))],
      ["nf", query(collection(db, "nuoveForniture"), where("organizationId", "==", o))],
      ["richiesteMateriali", query(collection(db, "richiesteMateriali"), where("organizationId", "==", o))],
      ["pratiche", query(collection(db, "pratiche"), where("organizationId", "==", o), where("stato", "==", "aperta"))],
      ["documentiPratiche", query(collection(db, "documentiPratiche"), where("organizationId", "==", o), where("riservato", "==", false))],
      ["clienti", query(collection(db, "clienti"), where("organizationId", "==", o))],
      ["noleggi", query(collection(db, "noleggi"), where("orgId", "==", o))],
      ["varianti", query(collection(db, "attivitaExtra"), where("orgId", "==", o))],
      ["richiesteFatturazione", query(collection(db, "richiesteFatturazione"), where("organizationId", "==", o))],
    ];
    const unsubs = fonti.map(([k, q]) => onSnapshot(q,
      s => { setD(x => ({ ...x, [k]: s.docs.map(dd => ({ id: dd.id, ...dd.data() })) })); setArrivati(x => ({ ...x, [k]: true })); setAggiornatoAlle(new Date()); },
      () => { setD(x => ({ ...x, [k]: [] })); setArrivati(x => ({ ...x, [k]: true })); } // es. modulo non incluso nel piano: si ignora
    ));
    return () => unsubs.forEach(u => u());
  }, [effectiveOrgId, autorizzato]);

  const pronto = ["projects", "tasks"].every(k => arrivati[k]);
  const economiaPronta = ["projects", "budget", "movimenti", "ordini", "varianti"].every(k => arrivati[k]);
  const oggi = toLocalDateStr(new Date());
  const tra7 = toLocalDateStr(new Date(Date.now() + 7 * 86400000));

  const progetti = d.projects || VUOTO;
  const tasksL = d.tasks || VUOTO;
  const subtasksL = d.subtasks || VUOTO;
  const scadL = d.scadenzario || VUOTO;
  const noteSpeseL = d.noteSpese || VUOTO;
  const movL = d.movimenti || VUOTO;
  const budgetL = d.budget || VUOTO;
  const ordiniL = d.ordini || VUOTO;
  const nfL = d.nf || VUOTO;
  const richMatL = d.richiesteMateriali || VUOTO;
  const praticheL = d.pratiche || VUOTO;
  const docPratL = d.documentiPratiche || VUOTO;
  const clientiL = d.clienti || VUOTO;
  const noleggiL = d.noleggi || VUOTO;
  const variantiL = d.varianti || VUOTO;
  const richFattL = d.richiesteFatturazione || VUOTO;
  const aperte = useMemo(() => progetti.filter(commessaAperta), [progetti]);

  // ── 1. Priorità operative ─────────────────────────────────────────────────────
  const elenchi = useMemo(() => elenchiPriorita(vociCommesseAperte({ tasks: tasksL, subtasks: subtasksL, projects: progetti })),
    [tasksL, subtasksL, progetti]);
  const scadenzario = useMemo(() => {
    const aperteSc = scadL.filter(s => !s.eseguita && s.dataScadenza);
    return { scadute: aperteSc.filter(s => s.dataScadenza < oggi), entro7: aperteSc.filter(s => s.dataScadenza >= oggi && s.dataScadenza <= tra7) };
  }, [scadL, oggi, tra7]);
  const materiali = useMemo(() => {
    const nf = nfL.filter(n => !n.eliminata && statoNf(n) === "Da gestire");
    const mag = richMatL.filter(r => r.stato === "in_attesa");
    return { nf, mag };
  }, [nfL, richMatL]);

  // ── 2. Economia delle commesse aperte ─────────────────────────────────────────
  const fontOre = fonteOreAzienda(orgVisitata);
  const economia = useMemo(() => {
    if (!economiaPronta) return null;
    const varPer = {};
    variantiL.forEach(v => { if (v.projectId) (varPer[v.projectId] = varPer[v.projectId] || []).push(v); });
    const budgetPer = {};
    budgetL.forEach(b => { if (b.projectId && !budgetPer[b.projectId]) budgetPer[b.projectId] = b; });
    const movPer = {};
    movL.forEach(m => { if (m.projectId) (movPer[m.projectId] = movPer[m.projectId] || []).push(m); });
    let totale = 0, conBudget = 0, inAttesa = 0;
    const senzaBudget = [], fuoriBudget = [];
    aperte.forEach(p => {
      const rV = riepilogoVarianti(varPer[p.id] || []);
      inAttesa += rV.inAttesa || 0;
      const bud = budgetPer[p.id] ? { ...budgetPer[p.id], variantiCalcolate: rV } : null;
      if (!bud) { senzaBudget.push(p); return; }
      const eco = economiaSintetica({ budget: bud, movimenti: movPer[p.id] || [], ordini: ordiniL, projectId: p.id, fontOre });
      const mp = marginePrevistoCommessa({ budget: bud, ricavi: eco.ricavi, costiReali: eco.costi });
      if (mp.marginePrevisto === null) { senzaBudget.push(p); return; }
      totale += mp.marginePrevisto; conBudget++;
      if (eco.costi > mp.costoPrevisto) fuoriBudget.push({ p, costi: eco.costi, previsto: mp.costoPrevisto });
    });
    // W453: manodopera e noleggi valgono all'ultima apertura del Controllo Economico di ogni commessa
    const aggiornatiAl = testoAggiornamentoCosti(aperte.map(p => dataAggiornamentoCosti(budgetPer[p.id])));
    return { totale: Math.round(totale), conBudget, senzaBudget, inAttesa: Math.round(inAttesa * 100) / 100, fuoriBudget, varPer, budgetPer, aggiornatiAl };
  }, [economiaPronta, aperte, variantiL, budgetL, movL, ordiniL, fontOre]);

  // Riallineamento dei riepiloghi varianti salvati sui budget (regole di accettazione di
  // ottobre 2026): una volta per apertura, solo dove il salvato non coincide più. Così anche
  // Centro Operativo, Cabina di Regia e le altre viste leggono il totale giusto.
  const riallineato = useRef(false);
  useEffect(() => {
    if (!economia || riallineato.current || inVisita) return;
    riallineato.current = true;
    progetti.forEach(p => {
      const rV = riepilogoVarianti(economia.varPer[p.id] || []);
      const salvato = economia.budgetPer[p.id]?.variantiCalcolate;
      if (rV.n === 0 && !salvato) return;
      if (salvato && stessoRiepilogo(salvato, rV)) return;
      sincronizzaVariantiSuBudget(p.id, effectiveOrgId).catch(() => {});
    });
  }, [economia, inVisita, progetti, effectiveOrgId]);

  // ── Variazione: confronto con l'ultima rilevazione salvata (non con «ieri» presunto) ──
  // insightRilevazioni/{orgId}.giorni["AAAA-MM-GG"] = { margine, commesse, alle }: la prima
  // apertura del giorno di un titolare o admin salva il valore; la freccia confronta con la
  // rilevazione più recente di un giorno precedente, sempre con la sua data e ora.
  const rilevato = useRef(false);
  useEffect(() => {
    if (!effectiveOrgId || !autorizzato) return;
    let vivo = true;
    getDoc(doc(db, "insightRilevazioni", effectiveOrgId))
      .then(s => { if (vivo) setRilevazioni(s.exists() ? (s.data().giorni || {}) : {}); })
      .catch(() => { if (vivo) setRilevazioni({}); });
    return () => { vivo = false; };
  }, [effectiveOrgId, autorizzato]);
  useEffect(() => {
    if (!economia || rilevazioni === null || rilevato.current || inVisita || economia.conBudget === 0) return;
    rilevato.current = true;
    if (rilevazioni[oggi]) return;
    const adesso = new Date();
    const voce = { margine: economia.totale, commesse: economia.conBudget, alle: `${p2(adesso.getHours())}:${p2(adesso.getMinutes())}` };
    const vecchie = Object.keys(rilevazioni).sort().slice(0, Math.max(0, Object.keys(rilevazioni).length - 59));
    setDoc(doc(db, "insightRilevazioni", effectiveOrgId), {
      orgId: effectiveOrgId, giorni: { [oggi]: voce, ...Object.fromEntries(vecchie.map(k => [k, deleteField()])) },
    }, { merge: true }).catch(() => {});
  }, [economia, rilevazioni, inVisita, oggi, effectiveOrgId]);
  const precedente = useMemo(() => {
    if (!rilevazioni) return null;
    const giorno = Object.keys(rilevazioni).filter(k => k < oggi).sort().pop();
    return giorno ? { giorno, ...rilevazioni[giorno] } : null;
  }, [rilevazioni, oggi]);

  // ── 3. Da sistemare: un elenco solo, le critiche prima ───────────────────────
  const daSistemare = useMemo(() => {
    const l = [];
    (economia?.fuoriBudget || []).forEach(({ p, costi, previsto }) => l.push({ peso: "critico",
      titolo: `«${p.name}» ha superato il costo previsto`,
      testo: `Costi ${euro(costi)} su ${euro(previsto)} previsti (+${Math.round((costi - previsto) / previsto * 100)}%)`,
      vai: { page: "project", projectId: p.id, initialTab: "economico" }, cta: "Controllo Economico" }));
    // Pratiche ferme oltre il previsto o con scadenze vicine
    const oggiP = oggiIso();
    praticheL.map(p => {
      const av = avanzamento(righePratica(p, docPratL, { vediRiservati: false }));
      return { p, ...statoIter(p, { documentiCompleti: av.completa, oggi: oggiP, inizioIso: p.createdAt?.toDate ? oggiIso(p.createdAt.toDate()) : oggiP }) };
    }).filter(x => x.attuale?.inRitardo || x.scadenze.length).forEach(x => l.push({ peso: x.attuale?.escalation ? "critico" : "alto",
      titolo: `Pratica «${x.p.titolo}» da sbloccare`,
      testo: x.attuale?.inRitardo ? `Ferma da ${x.attuale.giorniFerma} giorni nella fase «${x.attuale.fase.label}»` : `${x.scadenze[0].label} ${dataBreve(x.scadenze[0].data)}`,
      vai: { page: "pratiche-aperte" }, cta: "Pratiche" }));
    // Noleggi: rettifiche da vedere e mezzi oltre la fine prevista
    const rett = noleggiL.filter(n => n.rettificaDaVedere).map(n => ({ n, r: rettificheDaVedere(n)[0] })).filter(x => x.r);
    if (rett.length) l.push({ peso: "alto", titolo: `${rett.length} rettific${rett.length === 1 ? "a" : "he"} sui costi dei noleggi da vedere`,
      testo: rett.slice(0, 2).map(({ n, r }) => `${etichettaNoleggio(n)}: ${testoRettifica(r)}`).join(" · "), vai: { page: "registro-noleggi" }, cta: "Registro noleggi" });
    const oltre = noleggiL.map(n => ({ n, s: scadenzaNoleggio(n) })).filter(x => x.s && (x.s.stato === "oltre" || x.s.stato === "senza_data"));
    if (oltre.length) l.push({ peso: "alto", titolo: `${oltre.length} noleggi${oltre.length === 1 ? "o" : ""} oltre la fine prevista`,
      testo: oltre.slice(0, 2).map(({ n, s }) => `${etichettaNoleggio(n)}: ${s.stato === "oltre" ? `${s.giorni} gg oltre` : `aperto da ${s.giorni} gg`}, ${euro(s.costo)}`).join(" · "),
      vai: { page: "registro-noleggi" }, cta: "Registro noleggi" });
    const ns = noteSpeseL.filter(n => n.stato === "inviata");
    if (ns.length) l.push({ peso: "alto", titolo: `${ns.length} not${ns.length === 1 ? "a" : "e"} spese da approvare`,
      testo: ns.slice(0, 4).map(n => n.dipendenteNome || "Dipendente").join(", "), vai: { page: "nota-spese" }, cta: "Note spese" });
    if (elenchi.ferme.length) l.push({ peso: "medio", titolo: `${elenchi.ferme.length} attività in corso ferm${elenchi.ferme.length === 1 ? "a" : "e"} da oltre 14 giorni`,
      testo: "Nessun aggiornamento da più di due settimane", vai: { page: "control-center", elenco: "ferme" }, cta: "Elenco" });
    const conAttivita = new Set(tasksL.filter(t => !t.archived).map(t => t.projectId));
    const senzaAtt = aperte.filter(p => !conAttivita.has(p.id));
    if (senzaAtt.length) l.push({ peso: "medio", titolo: `${senzaAtt.length} commess${senzaAtt.length === 1 ? "a aperta" : "e aperte"} senza attività`,
      testo: senzaAtt.slice(0, 3).map(p => p.name).join(", "), vai: senzaAtt.length === 1 ? { page: "project", projectId: senzaAtt[0].id } : { page: "control-center" }, cta: senzaAtt.length === 1 ? "Apri" : "Centro Operativo" });
    return l.sort((a, b) => PESO[a.peso] - PESO[b.peso]);
  }, [economia, praticheL, docPratL, noleggiL, noteSpeseL, tasksL, elenchi, aperte]);

  // ── Fatturazione e ordini: soldi da far entrare e soldi già impegnati con i fornitori ──
  // Entrate: richieste di fatturazione in attesa (come il Registro fatturazione) + varianti
  // accettate ed eseguite non ancora fatturate. Uscite: ordini ai fornitori inviati e non
  // ancora ricevuti, IVA esclusa (stessa regola dei costi di commessa).
  const soldi = useMemo(() => {
    const ric = richFattL.filter(r => r.stato === "da_fatturare" || r.stato === "in_lavorazione");
    const rV = riepilogoVarianti(variantiL);
    const oda = ordiniL.filter(o => !o.eliminato && ["Inviato", "Confermato", "Parzialmente ricevuto"].includes(o.stato));
    return {
      richieste: ric.reduce((t, r) => t + numIt(r.importoRichiesto), 0), nRichieste: ric.length,
      varianti: rV.daFatturare || 0, nVarianti: rV.nDaFatturare || 0,
      ordini: oda.reduce((t, o) => t + imponibileOda(o), 0), nOrdini: oda.length,
    };
  }, [richFattL, variantiL, ordiniL]);

  // Clienti con commesse ma nessuna nuova da oltre 90 giorni → un numero nella Sezione Commerciale
  const clientiInattivi = useMemo(() => {
    const limite = Date.now() - 90 * 86400000;
    return clientiL.filter(cl => {
      const sue = progetti.filter(p => p.clienteId === cl.id && !p.isParent);
      if (!sue.length) return false;
      const ultima = Math.max(...sue.map(p => p.createdAt?.toDate?.()?.getTime() || 0));
      return ultima > 0 && ultima < limite;
    }).length;
  }, [clientiL, progetti]);

  const vai = dest => dest && onNavigate && onNavigate(dest);

  if (!autorizzato) return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", color: "#9ca3af", padding: 40, textAlign: "center" }}>
      <div style={{ fontSize: 44, marginBottom: 14 }}>🔒</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: "#374151", marginBottom: 6 }}>Accesso riservato</div>
      <div style={{ fontSize: 13, maxWidth: 360 }}>Nexiora Insight contiene dati aziendali sensibili (economici e commerciali): è visibile solo al titolare e agli amministratori dell'azienda.</div>
    </div>
  );

  const stretto = larghezza < 900;
  const visibili = mostraTutto ? daSistemare : daSistemare.slice(0, 6);
  const nMateriali = materiali.nf.length + materiali.mag.length;
  const diff = economia && precedente && economia.conBudget > 0 ? economia.totale - precedente.margine : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden", background: C.sfondo }}>
      {/* Fascia */}
      <div style={{ background: C.fascia, color: "#fff", padding: stretto ? "6px 14px" : "0 64px 0 20px", minHeight: 48, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <h2 style={{ fontSize: 16, fontWeight: 800, margin: 0, color: "#fff" }}>Nexiora Insight</h2>
          <BadgePiano funzione="insight" piano={piano} giorniRimasti={giorniRimasti} />
        </div>
        <span style={{ fontSize: 11.5, color: "rgba(255,255,255,.85)" }}>
          {new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}
          {aggiornatoAlle ? ` · dati aggiornati alle ${aggiornatoAlle.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}` : ""}
        </span>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: stretto ? 14 : 20 }}>
        {!pronto ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 60, color: "#9ca3af" }}>
            <RefreshCw size={20} style={{ animation: "spin 1s linear infinite", marginRight: 10 }} /> Lettura dei dati…
          </div>
        ) : (<>
          <div style={{ display: "grid", gridTemplateColumns: stretto ? "1fr" : "minmax(0,2fr) minmax(260px,1fr)", gap: 14, marginBottom: 14 }}>
            {/* Priorità operative */}
            <Scheda titolo="Priorità operative" destra={<span style={{ fontSize: 11, color: C.tenue }}>commesse aperte · clic per l'elenco</span>}>
              <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit,minmax(${stretto ? 100 : 112}px,1fr))`, gap: 8 }}>
                <Priorita valore={elenchi.inRitardo.length} etichetta="In ritardo" rosso sotto="attività e sottoattività"
                  onClick={() => vai({ page: "control-center", elenco: "inRitardo" })} titoloAiuto="Scadute e non completate, annullate o sospese" />
                <Priorita valore={elenchi.scadonoOggi.length} etichetta="Scadono oggi"
                  onClick={() => vai({ page: "control-center", elenco: "scadonoOggi" })} />
                <Priorita valore={elenchi.nonAssegnate.length} etichetta="Senza responsabile" sotto="in scadenza entro 7 gg"
                  onClick={() => vai({ page: "control-center", elenco: "nonAssegnate" })} />
                <Priorita valore={nMateriali} etichetta="Materiali in attesa"
                  sotto={[materiali.nf.length ? `${materiali.nf.length} NF da prendere in carico` : "", materiali.mag.length ? `${materiali.mag.length} al magazzino` : ""].filter(Boolean).join(" · ")}
                  onClick={() => vai({ page: materiali.nf.length ? "centro-acquisti" : "magazzino" })} />
                <Priorita valore={scadenzario.scadute.length + scadenzario.entro7.length} etichetta="Scadenzario" rosso={scadenzario.scadute.length > 0}
                  sotto={scadenzario.scadute.length ? `${scadenzario.scadute.length} già scadut${scadenzario.scadute.length === 1 ? "a" : "e"}` : "entro 7 giorni"}
                  onClick={() => vai({ page: "scadenziario" })} />
              </div>
            </Scheda>

            {/* Economia delle commesse aperte */}
            <Scheda titolo="Margine previsto · commesse aperte">
              {!economia ? <div style={{ fontSize: 12, color: C.tenue }}>Calcolo…</div> : economia.conBudget === 0 ? (
                <div style={{ fontSize: 12.5, color: C.testo, lineHeight: 1.5 }}>
                  Nessuna commessa aperta ha ancora un budget di costo (personale e materiali previsti): senza, il margine previsto non si può calcolare.
                </div>
              ) : (<>
                <div style={{ fontSize: 26, fontWeight: 800, color: economia.totale >= 0 ? "#111827" : C.rosso, lineHeight: 1.1 }}>{euro(economia.totale)}</div>
                {diff !== null && (
                  <div style={{ fontSize: 12, fontWeight: 700, marginTop: 4, color: diff > 0 ? C.verde : diff < 0 ? C.rosso : C.tenue }}>
                    {diff > 0 ? "↑ +" : diff < 0 ? "↓ −" : "= "}{diff !== 0 ? euro(Math.abs(diff)).replace("€ ", "€ ") : "invariato"}
                    <span style={{ fontWeight: 500, color: C.tenue }}> rispetto al {dataBreve(precedente.giorno)} alle {precedente.alle}</span>
                  </div>
                )}
                <div style={{ fontSize: 11.5, color: C.tenue, marginTop: 6, lineHeight: 1.5 }}>
                  su {economia.conBudget} commess{economia.conBudget === 1 ? "a" : "e"} con budget di costo
                  {economia.senzaBudget.length > 0 && ` · ${economia.senzaBudget.length} senza budget, esclus${economia.senzaBudget.length === 1 ? "a" : "e"}`}
                </div>
                {economia.aggiornatiAl && (
                  <div style={{ fontSize: 11, color: C.tenue, marginTop: 4, lineHeight: 1.5 }}>
                    Manodopera e noleggi: costi aggiornati all'apertura del Controllo Economico di ogni commessa (la meno recente il {economia.aggiornatiAl}).
                  </div>
                )}
              </>)}
              {economia && economia.inAttesa > 0 && (
                <div style={{ fontSize: 11.5, color: C.arancio, marginTop: 6 }}>Varianti in attesa di accettazione: {euro(economia.inAttesa)} · fuori dal margine</div>
              )}
              <button onClick={() => vai({ page: "control-center", elenco: "commesse" })}
                style={{ marginTop: 10, background: "none", border: "none", padding: 0, color: C.link, fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 2, fontFamily: "'Inter',sans-serif" }}>
                Vedi le commesse <ChevronRight size={13} />
              </button>
            </Scheda>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: stretto ? "1fr" : "minmax(0,1fr) minmax(0,1fr)", gap: 14, marginBottom: 20, alignItems: "start" }}>
          {/* Da sistemare */}
          <Scheda titolo="Da sistemare"
            destra={daSistemare.length > 0 ? <span style={{ fontSize: 11, color: C.tenue }}>{daSistemare.length} punt{daSistemare.length === 1 ? "o" : "i"}</span> : null}>
            {daSistemare.length === 0 ? (
              <div style={{ fontSize: 12.5, color: C.verde, fontWeight: 600 }}>✓ Nulla da sistemare: budget, pratiche, noleggi, note spese e attività sono in ordine.</div>
            ) : (<>
              {visibili.map((x, i) => (
                <div key={i} onClick={() => vai(x.vai)}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 4px", borderTop: i ? `1px solid ${C.bordo}` : "none", cursor: "pointer" }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, background: x.peso === "critico" ? C.rosso : x.peso === "alto" ? C.arancio : "#9ca3af" }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{x.titolo}</div>
                    {x.testo && <div style={{ fontSize: 11.5, color: C.tenue, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: stretto ? "normal" : "nowrap" }}>{x.testo}</div>}
                  </div>
                  <span style={{ fontSize: 11.5, color: C.link, fontWeight: 700, whiteSpace: "nowrap", display: "flex", alignItems: "center" }}>{stretto ? "" : x.cta}<ChevronRight size={13} /></span>
                </div>
              ))}
              {daSistemare.length > 6 && (
                <button onClick={() => setMostraTutto(m => !m)}
                  style={{ marginTop: 6, background: "none", border: "none", color: C.link, fontSize: 12, fontWeight: 700, cursor: "pointer", padding: 0, fontFamily: "'Inter',sans-serif" }}>
                  {mostraTutto ? "Mostra meno" : `Mostra tutti (${daSistemare.length})`}
                </button>
              )}
            </>)}
          </Scheda>

          {/* Fatturazione e ordini */}
          <Scheda titolo="Fatturazione e ordini">
            {[
              { etichetta: "Da fatturare", valore: soldi.richieste + soldi.varianti, colore: "#111827",
                dettaglio: [soldi.nRichieste ? `${soldi.nRichieste} richiest${soldi.nRichieste === 1 ? "a" : "e"} di fatturazione ${euro(soldi.richieste)}` : "",
                  soldi.nVarianti ? `${soldi.nVarianti} variant${soldi.nVarianti === 1 ? "e accettata" : "i accettate"} ${euro(soldi.varianti)}` : ""].filter(Boolean).join(" · ") || "nulla in attesa",
                vai: { page: "da-fatturare" }, cta: "Richieste di fatturazione" },
              { etichetta: "Ordini ai fornitori in corso", valore: soldi.ordini, colore: "#111827",
                dettaglio: soldi.nOrdini ? `${soldi.nOrdini} ordin${soldi.nOrdini === 1 ? "e inviato" : "i inviati"} e non ancora ricevut${soldi.nOrdini === 1 ? "o" : "i"} · IVA esclusa` : "nessun ordine in attesa di consegna",
                vai: { page: "centro-acquisti" }, cta: "Centro Acquisti" },
            ].map((x, i) => (
              <div key={x.etichetta} onClick={() => vai(x.vai)} style={{ padding: "10px 4px", borderTop: i ? `1px solid ${C.bordo}` : "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.tenue }}>{x.etichetta}</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: x.colore, lineHeight: 1.25 }}>{euro(x.valore)}</div>
                  <div style={{ fontSize: 11.5, color: C.tenue }}>{x.dettaglio}</div>
                </div>
                <span style={{ fontSize: 11.5, color: C.link, fontWeight: 700, whiteSpace: "nowrap", display: "flex", alignItems: "center" }}>{stretto ? "" : x.cta}<ChevronRight size={13} /></span>
              </div>
            ))}
            <div style={{ fontSize: 10.5, color: "#9ca3af", marginTop: 6 }}>Il lavoro maturato nei SAL per cui nessuno ha ancora chiesto la fattura è in «Maturato non ancora richiesto», nella Sezione Commerciale: gli stessi soldi non compaiono in due posti.</div>
          </Scheda>
          </div>

          <div style={{ paddingTop: 6 }}>
            <SezioneCommerciale onNavigate={vai} clientiInattivi={clientiInattivi} />
          </div>
        </>)}
      </div>
    </div>
  );
}
